import React, { useState } from 'react';
import { Stack, Accordion, AccordionSummary, AccordionDetails, IconButton, Tooltip, LinearProgress, Divider, Box } from '@mui/material';
import { Typography, Button } from 'components/index';
import { ExpandMore as ExpandIcon, Delete, Error as ErrorIcon, CheckCircle } from '@mui/icons-material';
import { uploadFile, uploadFileSequentially } from './fileUpload.api';
import { toast } from 'react-toastify';
import Dropzone from '../Dropzone';
import { useQueryClient } from 'react-query';
import dataKeys from '../../common/react-query/data-keys';
import HelpText from './HelpText';
import { ROUTES } from '../../common/constants/urls';
import { FileUploadStatus, TypeOfFile } from './fileUpload.type';
import { handleError } from 'common/utils/util.errors';
import { useNavigate } from 'react-router-dom';
import { FileUploadErrorDTO } from '../project/project.type';

interface Props {
  onClose: () => void;
  useSequentialUpload?: boolean;
  filesToAccept: { [key: string]: string[] };
  typeOfFile: TypeOfFile;
  navigatePath: string;
  setDialogTitle: (title: React.ReactNode) => void;
  setDialogActions: (actions: React.ReactNode) => void;
}

export interface FilesListItem {
  file: File;
  status: keyof typeof FileUploadStatus;
  errors: FileUploadErrorDTO[];
  progress: number;
  projectId?: string;
  previewUrl?: string;
}

const FileUploadDialog: React.FC<Props> = ({
  onClose,
  useSequentialUpload = false,
  filesToAccept,
  typeOfFile,
  navigatePath,
  setDialogTitle,
  setDialogActions,
}) => {
  const [allFilesUploaded, setAllFilesUploaded] = useState(false);
  const [files, setFiles] = useState<FilesListItem[]>([]);
  const [uploadString, setUploadString] = useState('Upload All');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  React.useEffect(() => {
    setDialogTitle(
      typeOfFile === TypeOfFile.Project ? 'Upload File' : typeOfFile === TypeOfFile.CostAnalysis ? 'Upload Cost Analysis' : 'Upload File',
    );

    setDialogActions(
      <>
        <Button variant='outlined' color='secondary' onClick={handleClose}>
          Cancel
        </Button>
        <Button
          color='error'
          variant='outlined'
          disabled={!files || files.length === 0 || files.some(file => file.status === FileUploadStatus.Uploading)}
          onClick={removeAll}
        >
          Remove All
        </Button>
        <Button
          disabled={!files || files.length === 0 || files.some(file => file.status === FileUploadStatus.Uploading)}
          onClick={uploadString === 'Finish' ? handleFinish : handleUploadAll}
        >
          {uploadString}
        </Button>
      </>,
    );
  }, [files, uploadString]);

  React.useEffect(() => {
    const allUploaded =
      files.length > 0 &&
      files.every(file =>
        (['Success', 'SuccessWithWarning', 'Exists', 'Failed'] as Array<(typeof FileUploadStatus)[keyof typeof FileUploadStatus]>).includes(
          file.status,
        ),
      );
    setAllFilesUploaded(allUploaded);
    if (allUploaded) {
      setUploadString('Finish');
    }
    if (files.length == 0) {
      setUploadString('Upload All');
    }
  }, [files]);

  const updateFileItem = (
    fileName: string,
    status: keyof typeof FileUploadStatus,
    errors: FileUploadErrorDTO[],
    progress: number,
    projectId = '',
  ) => {
    setFiles(files =>
      files.map(item => {
        if (item.file.name === fileName) {
          return { ...item, status, errors, progress, projectId };
        }
        return item;
      }),
    );
  };

  const handleFileUpload = async (fileName: string, overwriteFlag = false) => {
    const file = files.find(item => item.file.name === fileName);
    if (!file) {
      toast.error('File not found', { autoClose: 2000 });
      return;
    }
    try {
      const response = await uploadFile([file.file], overwriteFlag, updateFileItem);
      const result = response.results[0];
      if (
        [FileUploadStatus.Success, FileUploadStatus.SuccessWithWarning, FileUploadStatus.Failed, FileUploadStatus.Exists].includes(
          result.statusString,
        )
      ) {
        updateFileItem(result.fileName, result.statusString, result.errors, 100, result.projectId ?? '');
      }
      if (result.statusString === FileUploadStatus.Success) {
        queryClient.invalidateQueries(dataKeys.allProjects);
        queryClient.invalidateQueries(dataKeys.files);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleUploadAll = () => {
    if (useSequentialUpload) {
      uploadFileSequentially(
        files.map(f => f.file),
        false,
        updateFileItem,
      ).catch(error => {
        handleError(error);
      });
    } else {
      files.forEach(file => {
        handleFileUpload(file.file.name);
      });
    }
  };

  const handleDrop = (files: File[]) => {
    if (files.length > 0) {
      setFiles(files.map(file => ({ file, status: FileUploadStatus.Idle, errors: [], progress: 0 })));
    }
  };

  const handleClose = () => {
    if (files.some(file => file.status === FileUploadStatus.Uploading)) {
      toast.warn('Files are still uploading. Please wait until the upload is complete.');
      return;
    }
    onClose();
    setTimeout(() => {
      setFiles([]);
    }, 150);
  };

  const removeFile = (name: string) => {
    setFiles(files => files.filter(item => item.file.name !== name));
  };

  const removeAll = () => {
    setFiles([]);
  };
  const handleFinish = () => {
    navigate(navigatePath);
    handleClose();
  };

  const getButtonText = (fileStatus: string, fallBackText: string) => {
    if (fileStatus === FileUploadStatus.Uploading) return 'Uploading';
    if (fileStatus === FileUploadStatus.Parsing) return 'Parsing';
    return fallBackText;
  };

  return (
    <Box>
      <Stack spacing={2}>
        <Stack spacing={2} alignItems={'center'} justifyContent={'center'}>
          {files.length == 0 ? <Dropzone onDrop={handleDrop} multiple={true} accept={filesToAccept} /> : null}
          {files.length > 0 ? (
            <Stack direction={'column'} alignSelf={'flex-start'} width={'100%'}>
              {files.map((item, index) => {
                return (
                  <>
                    <Stack key={index} direction={'row'} p={1}>
                      <Stack flexGrow={1}>
                        <Typography variant='body2' fontWeight='bold'>
                          {item.file.name}
                        </Typography>
                        <Typography variant='button'>
                          {item.file.size < 1000000
                            ? `${Math.round(item.file.size / 1000)} KB`
                            : `${Math.round(item.file.size / 1000000)} MB`}
                        </Typography>
                        {renderStatus(item, typeOfFile)}
                      </Stack>
                      {item.status == FileUploadStatus.Exists ? (
                        <Button
                          variant='text'
                          color='error'
                          sx={{
                            p: 1,
                            alignItems: item.status === FileUploadStatus.Exists ? 'flex-start' : 'center',
                            mt: item.status === FileUploadStatus.Exists ? '40px' : '0',
                          }}
                          onClick={() => handleFileUpload(item.file.name, true)}
                        >
                          {getButtonText(item.status, 'Overwrite')}
                        </Button>
                      ) : (
                        <Button
                          variant='text'
                          sx={{ p: 1 }}
                          disabled={!(item.status == FileUploadStatus.Idle)}
                          onClick={() => {
                            if (typeOfFile === TypeOfFile.Project) {
                              handleFileUpload(item.file.name); // Handle Project upload
                            } else {
                              uploadFileSequentially([item.file], false, updateFileItem);
                            }
                          }}
                        >
                          {getButtonText(item.status, 'Upload')}
                        </Button>
                      )}
                      <Tooltip title='Remove file' sx={{ marginLeft: 1 }}>
                        <IconButton
                          color='error'
                          size='small'
                          disabled={!(item.status == FileUploadStatus.Idle)}
                          sx={{
                            alignItems: item.status === FileUploadStatus.Exists ? 'flex-start' : 'center',
                            mt: item.status === FileUploadStatus.Exists ? '40px' : '0',
                          }}
                          onClick={() => {
                            removeFile(item.file.name);
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    {index != files.length ? <Divider sx={{ margin: 1 }} /> : null}
                  </>
                );
              })}
            </Stack>
          ) : (
            <Typography variant='body1'>No file selected</Typography>
          )}
        </Stack>
        {typeOfFile === TypeOfFile.Project && (
          <Accordion elevation={1}>
            <AccordionSummary expandIcon={<ExpandIcon />}>
              <b>Need Help?</b>
            </AccordionSummary>
            <AccordionDetails>
              <HelpText />
            </AccordionDetails>
          </Accordion>
        )}
      </Stack>
    </Box>
  );
};

export const renderStatus = (item: FilesListItem, typeOfFile: TypeOfFile) => {
  return (
    <>
      {item.status === FileUploadStatus.Uploading ? <LinearProgress variant='determinate' value={item.progress} /> : null}
      {item.status === FileUploadStatus.Parsing ? <LinearProgress variant='indeterminate' /> : null}
      {item.status === FileUploadStatus.Success && (
        <Stack direction='row' alignItems='center'>
          <CheckCircle sx={{ mr: 1 }} color={'success'} />
          <Typography variant='button' fontWeight='medium' color={'success'}>
            File uploaded successfully{' '}
            {typeOfFile === TypeOfFile.Project && (
              <a href={`${ROUTES.PROJECTS}/${item.projectId}`} target='_blank' rel='noreferrer'>
                (Click here to view)
              </a>
            )}
          </Typography>
        </Stack>
      )}
      {item.status === FileUploadStatus.Exists && (
        <Stack>
          <Stack direction='row' alignItems='center'>
            <ErrorIcon sx={{ mr: 1 }} color={'error'} />
            <Typography variant='button' fontWeight='medium' color='error'>
              File already exists
            </Typography>
          </Stack>
          <ul>
            <li>
              <Typography variant='button'>
                Existing project information will be retained if the incoming project has empty fields.
              </Typography>
            </li>
            <li>
              <Typography variant='button'>Project draws will be fully replaced with the new ones from the incoming project.</Typography>
            </li>
          </ul>
        </Stack>
      )}
      {item.status === FileUploadStatus.Failed && (
        <Stack>
          <Stack direction='row' alignItems='center'>
            <ErrorIcon sx={{ mr: 1 }} color={'error'} />
            <Typography variant='button' fontWeight='medium' color='error'>
              File upload failed
            </Typography>
          </Stack>
          <ul>
            {item.errors.map((error, index) => (
              <li key={index}>
                <Typography variant='button'>{error.message}</Typography>
              </li>
            ))}
          </ul>
        </Stack>
      )}
      {item.status === FileUploadStatus.SuccessWithWarning && (
        <Stack>
          <Stack direction='row' alignItems='center'>
            <ErrorIcon sx={{ mr: 1 }} color={'error'} />
            <Typography variant='button' fontWeight='medium' color={'warning'}>
              File uploaded with warnings{' '}
              {typeOfFile === TypeOfFile.Project && (
                <a href={`${ROUTES.PROJECTS}/${item.projectId}`} target='_blank' rel='noreferrer'>
                  (Click here to view)
                </a>
              )}
            </Typography>
          </Stack>
          <ul>
            <li>
              <Typography variant='button'>Navigate to the project to view all warnings</Typography>
            </li>
          </ul>
        </Stack>
      )}
    </>
  );
};

export default FileUploadDialog;
